// Aplica um tema (cores em HEX/hex-named) ao :root via CSS variables HSL.
// Converte hex/rgb pra "H S% L%" pra que Tailwind use hsl(var(--xxx)).

export interface ThemeColors {
  background: string;
  foreground: string;
  card: string;
  cardForeground: string;
  popover?: string;            // opcional — se ausente, herda de card
  popoverForeground?: string;  // opcional — se ausente, herda de cardForeground
  primary: string;
  primaryForeground: string;
  secondary: string;
  secondaryForeground: string;
  muted: string;
  mutedForeground: string;
  accent: string;
  accentForeground: string;
  destructive: string;
  destructiveForeground: string;
  border: string;
  input: string;
  ring: string;
  warning: string;
  success: string;
  info: string;
}

export interface Theme {
  _id: string;
  name: string;
  kind: 'prebuilt' | 'prebuilt-custom' | 'user-custom';
  isDark: boolean;
  colors: ThemeColors;
}

function hexToHsl(hex: string): string {
  hex = hex.trim().replace('#', '');
  if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
  if (hex.length !== 6) return '0 0% 0%';
  const r = parseInt(hex.slice(0, 2), 16) / 255;
  const g = parseInt(hex.slice(2, 4), 16) / 255;
  const b = parseInt(hex.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

const CSS_VAR_MAP: Record<keyof ThemeColors, string> = {
  background: '--background',
  foreground: '--foreground',
  card: '--card',
  cardForeground: '--card-foreground',
  popover: '--popover',
  popoverForeground: '--popover-foreground',
  primary: '--primary',
  primaryForeground: '--primary-foreground',
  secondary: '--secondary',
  secondaryForeground: '--secondary-foreground',
  muted: '--muted',
  mutedForeground: '--muted-foreground',
  accent: '--accent',
  accentForeground: '--accent-foreground',
  destructive: '--destructive',
  destructiveForeground: '--destructive-foreground',
  border: '--border',
  input: '--input',
  ring: '--ring',
  warning: '--warning',
  success: '--success',
  info: '--info'
};

export function applyTheme(theme: Theme) {
  const root = document.documentElement;
  // Aplica popover ANTES com fallback pra card (caso o tema seedado nao tenha o campo)
  const popover = theme.colors.popover ?? theme.colors.card;
  const popoverFg = theme.colors.popoverForeground ?? theme.colors.cardForeground;
  if (popover) root.style.setProperty('--popover', hexToHsl(popover));
  if (popoverFg) root.style.setProperty('--popover-foreground', hexToHsl(popoverFg));

  for (const [k, cssVar] of Object.entries(CSS_VAR_MAP) as [keyof ThemeColors, string][]) {
    if (k === 'popover' || k === 'popoverForeground') continue; // ja tratado acima com fallback
    const v = theme.colors[k];
    if (v) root.style.setProperty(cssVar, hexToHsl(v as string));
  }
  root.classList.toggle('dark', theme.isDark);
  root.setAttribute('data-theme', theme._id);
}

export const FALLBACK_THEME: Theme = {
  _id: 'wolf',
  name: 'Wolf (fallback)',
  kind: 'prebuilt',
  isDark: true,
  colors: {
    background: '#0c0a09',
    foreground: '#fafaf9',
    card: '#1c1917',
    cardForeground: '#fafaf9',
    popover: '#1c1917',
    popoverForeground: '#fafaf9',
    primary: '#dc2626',
    primaryForeground: '#fafaf9',
    secondary: '#292524',
    secondaryForeground: '#fafaf9',
    muted: '#292524',
    mutedForeground: '#a8a29e',
    accent: '#dc2626',
    accentForeground: '#fafaf9',
    destructive: '#7f1d1d',
    destructiveForeground: '#fafaf9',
    border: '#44403c',
    input: '#44403c',
    ring: '#dc2626',
    warning: '#f59e0b',
    success: '#16a34a',
    info: '#3b82f6'
  }
};
